import dayjs from 'dayjs';
import { useCallback, useEffect, useState } from 'react';
import { useAbortableTrigger } from '../../hooks/use-abortable-trigger';
import { useFetchAllContracts } from '../../hooks/use-fetch-all-contracts';
import {
  BillingType,
  ClientType,
  ContractType,
  InsurerType,
  PartnershipType,
  useGetAllClientsQuery,
  useGetAllInsurersQuery,
  useGetBillingTypesQuery,
  useGetClientInsurersByIdsLazyQuery,
  useGetPartnershipsQuery,
} from '../../types';
import { ClientInsurerIndexFilterModel } from './client-insurer-index-types';
import {
  applyContractFilters,
  ClientInsurerIndexRow,
  enrichRowsWithContracts,
  filterByIsActive,
  generateSkeletonRows,
  hasContractFilters,
  paginateItems,
} from './client-insurer-index-utils';

export type { ClientInsurerIndexRow } from './client-insurer-index-utils';

export interface PaginationModel {
  page: number;
  pageSize: number;
}

export interface ClientInsurerIndexQueriesResult {
  rows: ClientInsurerIndexRow[];
  rowCount: number;
  isLoading: boolean;
  hasError: boolean;
  clientsData: ClientType[];
  clientsError: boolean;
  insurersData: Omit<InsurerType, 'eligibilityRef' | 'memberIdRequired'>[];
  insurersError: boolean;
  partnershipsData: PartnershipType[];
  partnershipsError: boolean;
  billingTypesData: BillingType[];
  referenceDataLoading: boolean;
  refetch: () => void;
}

export const useClientInsurerIndexQueries = (
  filterParams: ClientInsurerIndexFilterModel,
  paginationModel: PaginationModel,
): ClientInsurerIndexQueriesResult => {
  const {
    data: clientsData,
    error: clientsError,
    loading: clientsLoading,
  } = useGetAllClientsQuery();

  const {
    data: insurersData,
    error: insurersError,
    loading: insurersLoading,
  } = useGetAllInsurersQuery();

  const { data: billingTypesData } = useGetBillingTypesQuery();

  const {
    data: partnershipsData,
    loading: partnershipsLoading,
    error: partnershipsError,
  } = useGetPartnershipsQuery({ variables: { type: 'billing' } });

  const [
    getClientInsurersByIds,
    { loading: clientInsurersLoading, error: clientInsurersError },
  ] = useGetClientInsurersByIdsLazyQuery();

  const {
    fetchAllContracts,
    loading: contractsLoading,
    error: contractsError,
  } = useFetchAllContracts();

  const [rows, setRows] = useState<ClientInsurerIndexRow[]>([]);
  const [rowCount, setRowCount] = useState(0);

  const fetchData = useCallback(
    async (signal: AbortSignal): Promise<void> => {
      const partnerships = partnershipsData?.getPartnerships ?? [];
      const { page, pageSize } = paginationModel;
      const startDate = dayjs().format('YYYY-MM-DD');

      const clearResults = (): void => {
        setRows([]);
        setRowCount(0);
      };

      const fetchContractsForCIs = (ciIds: number[]): Promise<ContractType[]> =>
        fetchAllContracts({
          startDate,
          includeVoided: false,
          clientsInsurerIds: ciIds,
          size: 100,
          ...(filterParams.partnershipIds.length > 0 && {
            partnershipIds: filterParams.partnershipIds,
          }),
          ...(filterParams.billingTypes.length > 0 && {
            billingTypes: filterParams.billingTypes,
          }),
        });

      try {
        const { data: ciResult } = await getClientInsurersByIds({
          variables: {
            ...(filterParams.clientIds.length > 0 && {
              clientIds: filterParams.clientIds,
            }),
            ...(filterParams.insurerIds.length > 0 && {
              insurerIds: filterParams.insurerIds,
            }),
          },
          context: { fetchOptions: { signal } },
        });

        if (signal.aborted) return;

        const filteredCIs = filterByIsActive(
          ciResult?.getClientInsurersByIds ?? [],
          filterParams.ciStatus,
        );

        if (filteredCIs.length === 0) {
          clearResults();
          return;
        }

        if (hasContractFilters(filterParams)) {
          const contracts = await fetchContractsForCIs(
            filteredCIs.map(ci => ci.id),
          );
          if (signal.aborted) return;

          const enriched = enrichRowsWithContracts(
            generateSkeletonRows(filteredCIs),
            contracts,
            partnerships,
          );
          const finalRows = applyContractFilters(enriched, filterParams);

          setRowCount(finalRows.length);
          setRows(paginateItems(finalRows, page, pageSize));
        } else {
          const pageCIs = paginateItems(filteredCIs, page, pageSize);
          const skeletonRows = generateSkeletonRows(pageCIs);

          if (skeletonRows.length === 0) {
            clearResults();
            return;
          }

          const contracts = await fetchContractsForCIs(
            skeletonRows.map(r => r.id),
          );
          if (signal.aborted) return;

          setRowCount(filteredCIs.length);
          setRows(
            enrichRowsWithContracts(skeletonRows, contracts, partnerships),
          );
        }
      } catch {
        if (!signal.aborted) {
          clearResults();
        }
      }
    },
    [
      filterParams,
      paginationModel,
      getClientInsurersByIds,
      fetchAllContracts,
      partnershipsData,
    ],
  );

  const refetch = useAbortableTrigger(fetchData);

  useEffect(() => {
    if (!partnershipsLoading) {
      refetch();
    }
  }, [refetch, partnershipsLoading]);

  const referenceDataLoading = clientsLoading || insurersLoading;

  const isLoading =
    clientInsurersLoading ||
    contractsLoading ||
    partnershipsLoading ||
    referenceDataLoading;

  const hasError =
    !!clientInsurersError ||
    !!contractsError ||
    !!partnershipsError ||
    !!clientsError ||
    !!insurersError;

  return {
    rows,
    rowCount,
    isLoading,
    hasError,
    clientsData: clientsData?.getAllClients ?? [],
    clientsError: !!clientsError,
    insurersData: insurersData?.getAllInsurers ?? [],
    insurersError: !!insurersError,
    partnershipsData: partnershipsData?.getPartnerships ?? [],
    partnershipsError: !!partnershipsError,
    billingTypesData: billingTypesData?.getBillingTypes ?? [],
    referenceDataLoading,
    refetch,
  };
};
